import React from 'react';

import { DataState, Skeleton, deriveDataState } from '../loading';

/**
 * The body of a dialog or drawer that fetches when it opens
 * (docs/platform-standards.md §14). It holds a minimum height, so the frame
 * is the same size whether the body is a skeleton, an error or the data, and
 * nothing resizes when the answer arrives.
 *
 *   <DialogLoadState loaded={loaded} loading={loading} error={loadError}
 *     label='runs' onRetry={retry} skeleton={<Skeleton variant='table-row' count={5} />}>
 *     ...the body...
 *   </DialogLoadState>
 *
 * `loaded` is "a read has answered for this record". Until it has, and until
 * a failure says otherwise, the body is waiting, so the first render after
 * the dialog opens is already the skeleton and never an empty state.
 * After a first answer a page change or reload keeps the old body under the
 * quiet refresh bar. `empty` is drawn only when `isEmpty` and a read has
 * answered.
 */
export default function DialogLoadState({
	loaded,
	loading = false,
	error = false,
	isEmpty = false,
	empty = null,
	label,
	errorDetail,
	onRetry,
	skeleton = <Skeleton variant='list-row' count={4} />,
	minHeight = 240,
	children,
}) {
	const waiting = loading || (!loaded && !error);
	return (
		<div className='dialog-load-state' style={{ minHeight }}>
			<DataState
				state={deriveDataState({ loading: waiting, error: error ? errorDetail || true : null, hasData: loaded, isEmpty })}
				hasData={loaded}
				isEmpty={isEmpty}
				skeleton={skeleton}
				empty={empty}
				error={errorDetail}
				onRetry={onRetry}
				label={label}
			>
				{children}
			</DataState>
		</div>
	);
}
